import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Layout } from "@/components/Layout";
import { ProgressBar } from "@/components/ProgressBar";
import { JobStatusBadge } from "@/components/Badges";
import { toast } from "sonner";
import { Plus, FileStack, CheckCircle2, Briefcase, TrendingUp, Loader2, ChevronRight, Upload, AlertTriangle } from "lucide-react";

const field = "w-full rounded-lg border border-white/15 bg-[#141414] px-3 py-2 text-sm text-[#f7f3ec] outline-none transition-colors placeholder:text-neutral-600 focus:border-[#c5a24a] focus:ring-1 focus:ring-[#c5a24a]";
const lbl = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-neutral-400";

const money = (n) => `$${Math.round(n || 0).toLocaleString()}`;

function Kpi({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-white/10 bg-[#1a1a1a] p-5 transition-all hover:-translate-y-0.5 hover:border-[#c5a24a]/40">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-widest text-neutral-500">{label}</span>
        <Icon className="h-4 w-4" style={{ color: "#c5a24a" }} />
      </div>
      <div className="mt-3 font-heading text-3xl font-bold tabular-nums text-[#f7f3ec]">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  useEffect(() => { load(); }, []);

  const total = projects?.length || 0;
  const active = projects?.filter((p) => p.status === "active").length || 0;
  const received = projects?.reduce((a, p) => a + (p.stats?.grand?.received || 0), 0) || 0;
  const returned = projects?.reduce((a, p) => a + (p.stats?.grand?.returned || 0), 0) || 0;
  const overBudget = projects?.filter((p) => p.budget?.over_budget) || [];

  return (
    <Layout>
      <div className="border-b border-white/10 bg-[#141414]/70 px-8 py-6 backdrop-blur-xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-[#f7f3ec]">Projects</h1>
            <p className="mt-1 text-sm text-neutral-500">As-built drafting engagements.</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="import-excel-btn" onClick={() => setShowImport(true)} className="flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2.5 text-sm font-semibold text-neutral-200 transition-colors hover:bg-white/5">
              <Upload className="h-4 w-4" /> Import Excel
            </button>
            <button data-testid="new-job-btn" onClick={() => setShowCreate(true)} className="gold-gradient flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-[#121212] transition-opacity hover:opacity-90">
              <Plus className="h-4 w-4" strokeWidth={2.5} /> New Project
            </button>
          </div>
        </div>
      </div>

      <div className="p-8">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Kpi label="Projects" value={total} icon={Briefcase} testid="kpi-total-jobs" />
          <Kpi label="Active" value={active} icon={TrendingUp} testid="kpi-active-jobs" />
          <Kpi label="Drawings Received" value={received} icon={FileStack} testid="kpi-drawings" />
          <Kpi label="Returned" value={returned} icon={CheckCircle2} testid="kpi-completed" />
        </div>

        {overBudget.length > 0 && (
          <div data-testid="budget-alert-banner" className="mt-6 flex items-start gap-3 rounded-xl border border-red-500/40 bg-red-950/25 px-5 py-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
            <div className="text-sm">
              <span className="font-semibold text-red-300">{overBudget.length} project{overBudget.length > 1 ? "s" : ""} forecast over budget.</span>{" "}
              <span className="text-red-200/80">
                {overBudget.map((p, i) => (
                  <button key={p.id} onClick={() => navigate(`/projects/${p.id}`)} className="underline decoration-red-400/40 underline-offset-2 hover:text-red-100" data-testid={`budget-alert-link-${p.id}`}>
                    {p.name} <span className="font-semibold">(+{money(p.budget.overrun)})</span>{i < overBudget.length - 1 ? ", " : ""}
                  </button>
                ))}
              </span>
            </div>
          </div>
        )}

        <div className="mt-8 overflow-hidden rounded-xl border border-white/10 bg-[#1a1a1a]">
          <div className="grid grid-cols-12 gap-4 border-b border-white/10 bg-white/[0.03] px-5 py-3 text-xs font-semibold uppercase tracking-wider text-neutral-500">
            <div className="col-span-4">Project / Client</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-center">Completed</div>
            <div className="col-span-3">Overall Progress</div>
            <div className="col-span-1" />
          </div>

          {projects === null && <div className="flex justify-center py-16 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /></div>}
          {projects?.length === 0 && (
            <div className="py-16 text-center" data-testid="jobs-empty">
              <FileStack className="mx-auto h-10 w-10 text-neutral-700" />
              <p className="mt-3 text-sm text-neutral-500">No projects yet.</p>
              <button onClick={() => setShowCreate(true)} className="mt-4 text-sm font-semibold text-[#c5a24a] hover:text-[#d8b968]">Create your first project</button>
            </div>
          )}

          {projects?.map((p) => (
            <button key={p.id} data-testid={`job-row-${p.id}`} onClick={() => navigate(`/projects/${p.id}`)} className="group grid w-full grid-cols-12 items-center gap-4 border-b border-white/5 px-5 py-4 text-left transition-colors last:border-0 hover:bg-white/[0.03]">
              <div className="col-span-4">
                <div className="flex items-center gap-2">
                  <div className="font-heading text-base font-semibold text-[#f7f3ec]">{p.name}</div>
                  {p.budget?.over_budget && (
                    <span data-testid={`budget-flag-${p.id}`} title={`Forecast over budget by ${money(p.budget.overrun)}`} className="inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-950/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-300">
                      <AlertTriangle className="h-3 w-3" /> Over Budget
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-neutral-500">{p.job_number} · {p.client_name}</div>
              </div>
              <div className="col-span-2"><JobStatusBadge status={p.status} /></div>
              <div className="col-span-2 text-center text-sm tabular-nums text-neutral-400">
                <span className="font-semibold text-[#f7f3ec]">{p.stats?.grand?.completed || 0}</span>
                <span className="text-neutral-600"> / {p.stats?.grand?.received || 0}</span>
              </div>
              <div className="col-span-3"><ProgressBar value={p.stats?.overall_percent || 0} /></div>
              <div className="col-span-1 flex justify-end"><ChevronRight className="h-4 w-4 text-neutral-600 transition-colors group-hover:text-[#c5a24a]" /></div>
            </button>
          ))}
        </div>
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={(p) => { setShowCreate(false); toast.success("Project created"); navigate(`/projects/${p.id}`); }} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onImported={(p) => { setShowImport(false); navigate(`/projects/${p.id}`); }} />}
    </Layout>
  );
}

function ImportModal({ onClose, onImported }) {
  const [fileObj, setFileObj] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!fileObj) { toast.error("Choose an .xlsx file"); return; }
    setSaving(true);
    const fd = new FormData();
    fd.append("file", fileObj);
    try {
      const { data } = await api.post("/projects/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.areas_imported} areas, ${data.packages_imported} packages`);
      onImported(data.project);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl border border-white/10 bg-[#1a1a1a] shadow-2xl" data-testid="import-modal">
        <div className="border-b border-white/10 px-6 py-4"><h3 className="font-heading text-xl font-bold text-[#f7f3ec]">Import As-Built Tracker</h3></div>
        <form onSubmit={submit} className="space-y-4 px-6 py-5">
          <p className="text-sm text-neutral-400">Upload an existing As-Built Tracker spreadsheet (.xlsx). We'll create a new project and populate its areas and packages automatically.</p>
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-white/15 px-6 py-8 text-center transition-colors hover:border-[#c5a24a]/50">
            <Upload className="h-7 w-7 text-[#c5a24a]" />
            <span className="text-sm text-neutral-300">{fileObj ? fileObj.name : "Click to choose an .xlsx file"}</span>
            <input data-testid="import-file-input" type="file" accept=".xlsx,.xlsm" className="hidden" onChange={(e) => setFileObj(e.target.files[0])} />
          </label>
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="rounded-lg border border-white/15 px-4 py-2 text-sm font-medium text-neutral-300 transition-colors hover:bg-white/5">Cancel</button>
            <button data-testid="import-submit-btn" disabled={saving} type="submit" className="gold-gradient flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-[#121212] transition-opacity hover:opacity-90 disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Import</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", job_number: "", client_name: "", project_manager: "", scope_total: "", description: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...f, scope_total: parseInt(f.scope_total) || 0 };
      const { data } = await api.post("/projects", payload);
      onCreated(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl border border-white/10 bg-[#1a1a1a] shadow-2xl" data-testid="create-job-modal">
        <div className="border-b border-white/10 px-6 py-4"><h3 className="font-heading text-xl font-bold text-[#f7f3ec]">New Project</h3></div>
        <form onSubmit={submit} className="space-y-4 px-6 py-5">
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>Project Name</label><input data-testid="job-name-input" required className={field} value={f.name} onChange={set("name")} placeholder="Keyera KAPS As-Built" /></div>
            <div><label className={lbl}>Job Number</label><input data-testid="job-number-input" required className={field} value={f.job_number} onChange={set("job_number")} placeholder="23-343" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>Client</label><input data-testid="job-client-input" required className={field} value={f.client_name} onChange={set("client_name")} placeholder="Keyera" /></div>
            <div><label className={lbl}>Project Manager</label><input className={field} value={f.project_manager} onChange={set("project_manager")} placeholder="Iain Munro" /></div>
          </div>
          <div><label className={lbl}>Total Scope (drawings)</label><input type="number" className={field} value={f.scope_total} onChange={set("scope_total")} placeholder="3464" /></div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-white/15 px-4 py-2 text-sm font-medium text-neutral-300 transition-colors hover:bg-white/5">Cancel</button>
            <button data-testid="job-create-submit" disabled={saving} type="submit" className="gold-gradient flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-[#121212] transition-opacity hover:opacity-90 disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</button>
          </div>
        </form>
      </div>
    </div>
  );
}
